import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import { User, UserStakes } from "@/CodegenTypes";
import Clusterize from "clusterize.js";

import xlsx from "xlsx";

import { sumBy, first, sortBy } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import { formatCount } from "@/helpers/formatCount";
import NumberStakeModal from "@/components/modules/bet-details/NumberStakeModal.vue";
import { customMoment } from "@/main";
import { useTranslation } from "@/store/translation";
import InputText from "primevue/inputtext/InputText";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute } from "vue-router/composables";

type SortMode =
  | "number-asc"
  | "number-desc"
  | "inBig-asc"
  | "inBig-desc"
  | "inSmall-asc"
  | "inSmall-desc"
  | "inAmount-asc"
  | "inAmount-desc"
  | "intakeBig-asc"
  | "intakeBig-desc"
  | "intakeSmall-asc"
  | "intakeSmall-desc"
  | "intakeAmount-asc"
  | "intakeAmount-desc"
  | "outBig-asc"
  | "outBig-desc"
  | "outSmall-asc"
  | "outSmall-desc"
  | "outAmount-asc"
  | "outAmount-desc"
  | "maxPayout-asc"
  | "maxPayout-desc";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'UserStake',
  setup(__props) {

const route = useRoute();
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const numberFilter = ref("");
const selectedNumber = ref<null | string>(null);
const showNumberStakeModal = ref(false);
const sortMode = ref<SortMode>("number-asc");
const clusterize = ref<Clusterize | null>(null);
let clusterizeMounted = false;

const selectedUserId = computed(() => getRouteQueryValue(route.query.userId));
const selectedReleaseDate = computed(() =>
  getRouteQueryValue(route.query.startReleaseDate)
);
const userVariables = computed(() => {
  return {
    userId: selectedUserId.value,
  };
});
const userStakesVariables = computed(() => {
  return {
    releaseDate: customMoment(selectedReleaseDate.value).startOf("day").toDate(),
    behalfOf: selectedUserId.value,
  };
});

const { result: userResult, loading: userLoading } = useQuery<{ user: User }>(
  queries.user,
  userVariables,
  {
    enabled: computed(() => !!selectedUserId.value),
    fetchPolicy: "cache-and-network",
  }
);
const { result: userStakesResult, loading: userStakesLoading } = useQuery<{
  userStakes: UserStakes;
}>(queries.userStakes, userStakesVariables, {
  enabled: computed(() => !!selectedReleaseDate.value && !!selectedUserId.value),
  fetchPolicy: "cache-and-network",
});

const user = computed(() => userResult.value?.user ?? null);
const userStakes = computed(() => userStakesResult.value?.userStakes ?? null);
const apolloLoading = computed(
  () => userLoading.value || userStakesLoading.value
);
const stakeRows = computed(() => {
  if (!userStakes.value) {
    return [];
  }
  const downlineEatStake = userStakes.value.downlineEatStake;
  const downlineEatStakeMap = new Map();
  downlineEatStake.forEach((s) => {
    downlineEatStakeMap.set(s.number, s);
  });
  // const companyFinalStake = userStakes.value.companyFinalStake;
  const rows = userStakes.value.masterStake
    .filter((row) => row.number.includes(numberFilter.value))
    .map((row) => {
      const intakeRow = downlineEatStakeMap.get(row.number);
      // const outRow = companyFinalStake[$index];
      const bigIn = row.big;
      const smallIn = row.small;
      const amountIn = bigIn * 1.6 + smallIn * 0.7;
      const bigIntake = intakeRow.big;
      const smallIntake = intakeRow.small;
      const amountIntake = bigIntake * 1.6 + smallIntake * 0.7;

      const bigOut = bigIn - bigIntake;
      const smallOut = smallIn - smallIntake;
      const amountOut = amountIn - amountIntake;
      const maxPayout = bigIn * 4000 + smallIn * 3000;
      return {
        number: row.number,
        bigIn,
        smallIn,
        amountIn,
        bigIntake,
        smallIntake,
        amountIntake,
        bigOut,
        smallOut,
        amountOut,
        maxPayout,
      };
    });
  return sortBy(rows, (row) => {
    switch (sortMode.value) {
      case "number-asc":
        return row.number;
      case "number-desc":
        return -row.number;
      case "inBig-asc":
        return row.bigIn;
      case "inBig-desc":
        return -row.bigIn;
      case "inSmall-asc":
        return row.smallIn;
      case "inSmall-desc":
        return -row.smallIn;
      case "inAmount-asc":
        return row.amountIn;
      case "inAmount-desc":
        return -row.amountIn;
      case "intakeBig-asc":
        return row.bigIntake;
      case "intakeBig-desc":
        return -row.bigIntake;
      case "intakeSmall-asc":
        return row.smallIntake;
      case "intakeSmall-desc":
        return -row.smallIntake;
      case "intakeAmount-asc":
        return row.amountIntake;
      case "intakeAmount-desc":
        return -row.amountIntake;
      case "outBig-asc":
        return row.bigOut;
      case "outBig-desc":
        return -row.bigOut;
      case "outSmall-asc":
        return row.smallOut;
      case "outSmall-desc":
        return -row.smallOut;
      case "outAmount-asc":
        return row.amountOut;
      case "outAmount-desc":
        return -row.amountOut;
      case "maxPayout-asc":
        return row.maxPayout;
      case "maxPayout-desc":
        return -row.maxPayout;
    }
  });
});
const stakeSummary = computed(() => {
  return {
    bigIn: sumBy(stakeRows.value, (r) => r.bigIn),
    smallIn: sumBy(stakeRows.value, (r) => r.smallIn),
    amountIn: sumBy(stakeRows.value, (r) => r.amountIn),
    bigIntake: sumBy(stakeRows.value, (r) => r.bigIntake),
    smallIntake: sumBy(stakeRows.value, (r) => r.smallIntake),
    amountIntake: sumBy(stakeRows.value, (r) => r.amountIntake),
    bigOut: sumBy(stakeRows.value, (r) => r.bigOut),
    smallOut: sumBy(stakeRows.value, (r) => r.smallOut),
    amountOut: sumBy(stakeRows.value, (r) => r.amountOut),
    // maxPayout: sumBy(stakeRows.value, (r) => r.maxPayout),
  };
});
const renderedRows = computed(() => {
  return stakeRows.value.map((row, $index) => {
    return `
      <tr class="text-center">
        <td>${$index + 1}</td>
        <td>
              <a
                id="${row.number}"
                class="font-bold text-primary-600 cursor-pointer number-row"
                >${row.number}</a
              >
            </td>
            <td>${formatCount(row.bigIn)}</td>
            <td>${formatCount(row.smallIn)}</td>
            <td>${formatDollar(row.amountIn)}</td>
            <td>${formatCount(row.bigIntake)}</td>
            <td>${formatCount(row.smallIntake)}</td>
            <td>${formatDollar(row.amountIntake)}</td>
            <td>${formatCount(row.bigOut)}</td>
            <td>${formatCount(row.smallOut)}</td>
            <td>${formatDollar(row.amountOut)}</td>
            <td>${formatDollar(row.maxPayout)}</td>
      </tr>
      `;
  });
});

watch(stakeRows, () => {
  if (clusterizeMounted) {
    doClusterize();
  }
});

onMounted(() => {
  clusterizeMounted = true;
  doClusterize();
});

onBeforeUnmount(() => {
  clusterizeMounted = false;
  clusterize.value?.destroy(true);
  clusterize.value = null;
});

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? first(query) : query;
}

function doClusterize() {
  if (clusterize.value) {
    clusterize.value.update(renderedRows.value);
    return;
  }

  clusterize.value = new Clusterize({
    rows: renderedRows.value,
    scrollId: "scrollArea",
    contentId: "contentArea",
    rows_in_block: 20,
    show_no_data_row: false,
    // blocks_in_cluster: 3,
    callbacks: {
      clusterChanged: bindNumberRows,
    },
  });
}

function bindNumberRows() {
  const numberRows = document.getElementsByClassName("number-row");
  for (const row of numberRows) {
    const numberRow = row as HTMLElement;
    numberRow.addEventListener("click", () => {
      handleSelectNumber(numberRow.id);
    });
  }
}

function formatDate(dateStr: string) {
  return customMoment(dateStr).format("DD MMM YYYY");
}

function handleSelectNumber(number: string) {
  selectedNumber.value = number;
  showNumberStakeModal.value = true;
}

function exportExcel() {
  if (!user.value || !stakeSummary.value) {
    return;
  }
  // const table = this.$refs.table;
  // const wb = xlsx.utils.table_to_book(table as HTMLElement, { raw: true });

  const wb = xlsx.utils.book_new();

  const ws_data = [] as string[][];
  const metaRow1 = [`Generated at ${customMoment()}`];
  ws_data.push(metaRow1);
  const metaRow2 = [
    `Account ${user.value.userId.toUpperCase()} ${formatDate(
      selectedReleaseDate.value || ""
    )} Stake`,
  ];
  ws_data.push(metaRow2);
  const headerRow1 = [
    "",
    "In",
    "In",
    "In",
    "Intake",
    "Intake",
    "Intake",
    "Out",
    "Out",
    "Out",
    "First Prize",
  ];
  ws_data.push(headerRow1);

  const headerRow2 = [
    "Number",
    "Big",
    "Small",
    "Amount",
    "Big",
    "Small",
    "Amount",
    "Big",
    "Small",
    "Amount",
    "Max Payout",
  ];
  ws_data.push(headerRow2);

  for (const row of stakeRows.value) {
    const rowData = [
      row.number,
      formatCount(row.bigIn),
      formatCount(row.smallIn),
      formatDollar(row.amountIn),
      formatCount(row.bigIntake),
      formatCount(row.smallIntake),
      formatDollar(row.amountIntake),
      formatCount(row.bigOut),
      formatCount(row.smallOut),
      formatDollar(row.amountOut),
      formatDollar(row.maxPayout),
    ];
    ws_data.push(rowData);
  }
  const footerData = [
    `Total (${stakeRows.value.length})`,
    formatCount(stakeSummary.value.bigIn),
    formatCount(stakeSummary.value.smallIn),
    formatDollar(stakeSummary.value.amountIn),
    formatCount(stakeSummary.value.bigIntake),
    formatCount(stakeSummary.value.smallIntake),
    formatDollar(stakeSummary.value.amountIntake),
    formatCount(stakeSummary.value.bigOut),
    formatCount(stakeSummary.value.smallOut),
    formatDollar(stakeSummary.value.amountOut),
    "",
    // formatDollar(stakeSummary.value.maxPayout),
  ];
  ws_data.push(footerData);

  const ws = xlsx.utils.aoa_to_sheet(ws_data);
  ws["!merges"] = [
    { s: { c: 0, r: 0 }, e: { c: 10, r: 0 } },
    { s: { c: 0, r: 1 }, e: { c: 10, r: 1 } },
    { s: { c: 1, r: 2 }, e: { c: 3, r: 2 } },
    { s: { c: 4, r: 2 }, e: { c: 6, r: 2 } },
    { s: { c: 7, r: 2 }, e: { c: 9, r: 2 } },
  ];
  xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");

  xlsx.writeFile(wb, `${user.value?.userId.toUpperCase()}-stake.xlsx`, {
    bookType: "xlsx",
  });
  return;
}

return { __sfc: true,route, $t, t, numberFilter, selectedNumber, showNumberStakeModal, sortMode, clusterize, clusterizeMounted, selectedUserId, selectedReleaseDate, userVariables, userStakesVariables, userResult, userLoading, userStakesResult, userStakesLoading, user, userStakes, apolloLoading, stakeRows, stakeSummary, renderedRows, getRouteQueryValue, doClusterize, bindNumberRows, formatDate, handleSelectNumber, exportExcel, formatDollar, formatCount, NumberStakeModal, InputText }
}

})